<template>
  <div ref="rootRef" :style="{ position: 'relative' }">
    <button
      title="Options"
      :style="{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: '30px',
        padding: '0 11px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '12.5px',
        fontWeight: 700,
        border: `1px solid ${open ? 'var(--p-primary-500)' : 'var(--p-content-border-color)'}`,
        background: 'transparent',
        color: 'var(--p-text-muted-color)',
      }"
      @click="open = !open"
    >
      <i class="pi pi-cog" :style="{ fontSize: '12px' }" />Options
    </button>
    <div
      v-if="open"
      :style="{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 900,
        width: '300px',
        background: 'var(--p-content-background)',
        border: '1px solid var(--p-content-border-color)',
        borderRadius: '12px',
        boxShadow: 'var(--p-overlay-popover-shadow, 0 18px 50px rgba(0,0,0,.35))',
        padding: '8px',
      }"
    >
      <div
        :style="{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px 8px',
          fontSize: '10.5px',
          fontWeight: 800,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: 'var(--p-surface-400)',
        }"
      >
        <span>Fields</span><span>{{ visibleCount }} shown</span>
      </div>
      <div :style="{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1px' }">
        <div
          v-for="c in offered"
          :key="c.key"
          draggable="true"
          :style="rowStyle(c)"
          @dragstart="onDragStart($event, c.key)"
          @dragover="onDragOver($event, c.key)"
          @drop="onDrop($event, c.key)"
          @dragend="onDragEnd"
        >
          <i class="pi pi-bars" title="Drag to reorder" :style="{ fontSize: '12px', color: 'var(--p-surface-400)', cursor: 'grab', flexShrink: 0 }" />
          <label :style="{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }">
            <span
              :style="{
                width: '16px',
                height: '16px',
                borderRadius: '5px',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: c.visible ? 'var(--p-primary-500)' : 'transparent',
                border: `1.5px solid ${c.visible ? 'var(--p-primary-600)' : 'var(--p-content-border-color)'}`,
                color: 'var(--p-primary-950)',
              }"
            >
              <i v-if="c.visible" class="pi pi-check" :style="{ fontSize: '9px' }" />
            </span>
            <input type="checkbox" :checked="c.visible" :style="{ display: 'none' }" @change="toggle(c.key)">
            <span
              :style="{
                fontSize: '12.5px',
                color: c.visible ? 'var(--p-text-color)' : 'var(--p-text-muted-color)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }"
            >{{ label(c.key) }}</span>
          </label>
          <button
            title="Sort"
            :disabled="!c.visible"
            :style="{ ...iconBtn(!c.visible), width: '26px', color: c.sort ? 'var(--p-primary-600)' : 'var(--p-surface-400)' }"
            @click="cycle(c.key)"
          >
            <i :class="`pi pi-${c.sort === 'asc' ? 'sort-amount-up' : c.sort === 'desc' ? 'sort-amount-down' : 'sort'}`" :style="{ fontSize: '12px' }" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// "Options" column manager — visibility, drag-reorder, and per-column sort for
// the table. Ported from collection-frame ColumnsMenu. Emits update:colConfig
// with the next array (React used a setColConfig updater).
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { withSort } from '@/admin-collection-layout/lib/collectionHelpers.js'

const props = defineProps({
  config: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  colConfig: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:colConfig'])

const open = ref(false)
const rootRef = ref(null)
const dragKey = ref(null)
const overKey = ref(null)

const label = (k) => (k === '__primary' ? props.config.primaryLabel || props.config.kindLabel || 'Name' : (props.fieldMeta[k] || {}).label || k)
// A field the search leaves out — an identifier, a Stripe key — is not offered
// as a column either.
const offered = computed(() => props.colConfig.filter((c) => !props.fieldMeta[c.key]?.searchHidden))
const visibleCount = computed(() => offered.value.filter((c) => c.visible).length)

const toggle = (key) => emit('update:colConfig', props.colConfig.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)))
const cycle = (key) => emit('update:colConfig', withSort(props.colConfig, key))
const reorder = (from, to) => {
  const cs = props.colConfig
  const i = cs.findIndex((c) => c.key === from)
  const j = cs.findIndex((c) => c.key === to)
  if (i < 0 || j < 0 || i === j) return
  const next = [...cs]
  const [it] = next.splice(i, 1)
  next.splice(j, 0, it)
  emit('update:colConfig', next)
}

const iconBtn = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  borderRadius: '5px',
  border: 0,
  background: 'transparent',
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? 'var(--p-content-border-color)' : 'var(--p-surface-400)',
})

function onDocDown(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) open.value = false
}
watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onDocDown)
  else document.removeEventListener('mousedown', onDocDown)
})
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocDown))

function onDragStart(e, key) {
  dragKey.value = key
  e.dataTransfer.effectAllowed = 'move'
}
function onDragOver(e, key) {
  e.preventDefault()
  if (overKey.value !== key) overKey.value = key
}
function onDrop(e, key) {
  e.preventDefault()
  if (dragKey.value) reorder(dragKey.value, key)
  dragKey.value = null
  overKey.value = null
}
function onDragEnd() {
  dragKey.value = null
  overKey.value = null
}
const rowStyle = (c) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '5px 8px',
  borderRadius: overKey.value === c.key && dragKey.value && dragKey.value !== c.key ? 0 : '8px',
  background: c.visible ? 'var(--p-highlight-background)' : 'transparent',
  opacity: dragKey.value === c.key ? 0.4 : 1,
  boxShadow: overKey.value === c.key && dragKey.value && dragKey.value !== c.key ? 'inset 0 2px 0 var(--p-primary-500)' : 'none',
})
</script>
